import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isEmbedded } from 'utils/proxyUtils';
import AppLoader from './AppLoader';
import ActivationUI from './components/ActivationUI';
import { BottomBar } from './components/bottom-bar/bottom-bar';
import { DevicePreview } from './components/bottom-bar/device-menu/device-preview';
import DisabledComments from './components/comments-disabled/comments-disabled';
import { ElementSelector } from './components/element-selector/element-selector';
import FixedUIContainer from './components/FixedUI';
import ImageLightbox from './components/lightbox';
import TaskDiscardAlertModal from './components/modals/discard-task-text/discard-task-modal';
import LockFeatureModal from './components/modals/lock-feature/lock-feature-modal';
import { LoginRegisterModal } from './components/modals/login-register/login-register-modal';
import RegisterCompanyDetailsModal from './components/modals/Register-company-details/register-company-detail-modal';
import { SiteSelectionModal } from './components/modals/site-selection-modal';
import GodaddyNameModal from './components/modals/update-username/update-godaddy-username';
import UpdateUsernameModal from './components/modals/update-username/update-username';
import ScrollableContainer from './components/ScrollableUI';
import { Sidebar } from './components/sidebar/sidebar';
import AnimatedScreenshot from './components/tasks/automated-screenshot/AnimatedScreenshot';
import { PointerAreaBox } from './components/tasks/pointer-area-box';
import { Stickers } from './components/tasks/stickers';
import { useAppEffects } from './hooks/use-app-effects';
import { useBottomBarLoading } from './hooks/use-bottom-bar-loading';
import useDarkMode from './hooks/use-dark-mode';
import { useInitialScreenshot } from './hooks/use-initial-screenshot';
import { useCollaborativeActiveTask } from './hooks/use-realtime-active-sticker';
import { useRealtimeAiReviewTask } from './hooks/use-realtime-ai-review-task';
import { useRealtimeComments } from './hooks/use-realtime-comments';
import { useRealtimeUserSiteSettings } from './hooks/use-realtime-site-settings';
import { useRealtimeTasks } from './hooks/use-realtime-tasks';
import { useRealtimeUserData } from './hooks/use-realtime-user-data';
import { useSidebarLoading } from './hooks/use-sidebar-loading';
import { useTaskDetails } from './hooks/use-task-details';
import { useTasksNew } from './hooks/use-tasks-new';
import { useAppDispatch } from './store';
import { bootstrapAction, isReady } from './store/bootstrapSlice';
import { getShowSiteSelectionModal } from './store/siteInfoSlice';
import { getAnimateScreenshot } from './store/tasksSlice';
import { getBrowseMode, getDisplayMode } from './store/uiSlice';
import { getLoggedIn, getShowForgotPassword, getShowLockFeature, getShowLogin, getShowRegister } from './store/userSlice';

const App = () => {
  const dispatch = useAppDispatch();
  // Bootstrap effect
  useEffect(() => {
    dispatch(bootstrapAction());
  }, [dispatch]);
  const ready = useSelector(isReady);

  useAppEffects();
  // Show loading fallback until the app is ready
  switch (ready) {
    case 'error':
      return null;
    case 'site-selection':
      return <SiteSelectionModal />;
    case 'plugin_activation':
      return <ActivationUI />;
  }

  return <AppContent />;
};

// Updated AppContent component section
const AppContent = () => {
  const loggedIn = useSelector(getLoggedIn);
  const showForgot = useSelector(getShowForgotPassword);
  const showLogin = useSelector(getShowLogin);
  const showRegister = useSelector(getShowRegister);
  const showSiteSelection = useSelector(getShowSiteSelectionModal);
  const browseMode = useSelector(getBrowseMode);
  const displayMode = useSelector(getDisplayMode);
  const animateScreenshot = useSelector(getAnimateScreenshot);
  const showLockFeature = useSelector(getShowLockFeature);
  const fixedUIContainerRef = useRef(null);
  const scrollableContainerRef = useRef(null);
  const { isLoading: isLoadingTasks } = useTasksNew();
  useDarkMode();
  useTaskDetails();
  useRealtimeTasks();
  useRealtimeUserData();
  useRealtimeComments();
  useRealtimeAiReviewTask();
  useCollaborativeActiveTask();
  useRealtimeUserSiteSettings();
  useInitialScreenshot();
  useSidebarLoading();
  useBottomBarLoading();

  return (
    <div
      id="atarim-collab-app"
      data-atarim
    >
      <AppLoader height={6} />
      <div className='isolate overflow-hidden w-full'>
        <ElementSelector />
      </div>

      {displayMode !== 'desktop' && (
        <DevicePreview />
      )}

      {animateScreenshot && <AnimatedScreenshot />}
      {/* Fixed UI elements that shouldn't scroll */}
      {!isEmbedded() && (
        <FixedUIContainer ref={fixedUIContainerRef}>

          <DisabledComments />
          <Sidebar constraintsRef={fixedUIContainerRef} isLoadingTasks={isLoadingTasks} />
          <BottomBar constraintsRef={fixedUIContainerRef} />
        </FixedUIContainer>
      )}

      {!browseMode && displayMode === 'desktop' && (
        <ScrollableContainer ref={scrollableContainerRef}>
          {loggedIn &&
            <PointerAreaBox />
          }
          {/* Unified Stickers component now handles both regular and AI tasks */}
          <Stickers />
        </ScrollableContainer>
      )}
      {/* Modals */}
      {(showForgot || showLogin || showRegister) && <LoginRegisterModal />}
      {showSiteSelection && <SiteSelectionModal />}
      <RegisterCompanyDetailsModal />
      <ImageLightbox />
      <UpdateUsernameModal />
      <GodaddyNameModal />
      {showLockFeature && <LockFeatureModal />}
      <TaskDiscardAlertModal />
    </div>
  );
};

export default App;